import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Alert from '@mui/material/Alert';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import Snackbar from '@mui/material/Snackbar';

import { useUpdateInformation } from '../api/mutations';
import { useInformationQuery } from '../api/queries';
import type { SettingsValues } from '../lib/settingsValues';
import { toInformationUpdate, toSettingsValues } from '../lib/settingsValues';
import LineConnectSection from './LineConnectSection';

function SettingsForm() {
    const { t, i18n } = useTranslation(undefined, { keyPrefix: 'Information' });
    const { t: tNotify } = useTranslation(undefined, { keyPrefix: 'Notification' });
    const { data: info, isError: loadFailed } = useInformationQuery();
    const updateInformation = useUpdateInformation();
    // Only the fields the user has changed; the rest show the account as cached.
    const [edits, setEdits] = useState<Partial<SettingsValues>>({});
    const [saveAlert, setSaveAlert] = useState<'success' | 'error' | null>(null);
    const values = { ...toSettingsValues(info), ...edits };

    const edit = (field: keyof SettingsValues) => (event: any) =>
        setEdits((prev) => ({ ...prev, [field]: event.target.value }));

    const timeZone = useMemo(() => {
        const resolvedZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const offsetMinutes = new Date().getTimezoneOffset();
        const absoluteMinutes = Math.abs(offsetMinutes);
        const hours = Math.floor(absoluteMinutes / 60)
            .toString()
            .padStart(2, '0');
        const minutes = (absoluteMinutes % 60).toString().padStart(2, '0');
        const offsetSign = offsetMinutes > 0 ? '-' : '+';
        return `${resolvedZone} (GMT${offsetSign}${hours}:${minutes})`;
    }, []);

    const inputBaseClasses =
        'w-full h-12 rounded-full border border-slate-200 bg-white px-4 text-base text-slate-900 shadow-sm transition-all focus:outline-none focus:ring-2 focus:ring-[#3F8EE8] md:h-11 md:w-[400px] md:rounded-lg md:border-slate-300 md:bg-white md:px-3 md:shadow-none md:focus:ring-1 md:focus:ring-[#1E2B7C]';
    const inputDisabledClasses = 'bg-slate-100 text-slate-500 cursor-not-allowed md:bg-[#E2E8F0] md:text-slate-400';

    const selectBaseSx = {
        borderRadius: { xs: '9999px', md: '0.75rem' },
        backgroundColor: { xs: '#FFFFFF', md: '#F1F5F9' },
        fontFamily: 'Roboto, sans-serif',
        fontSize: '1rem',
        '& .MuiSelect-select': {
            paddingY: { xs: '12px', md: '10px' },
            paddingX: { xs: '16px', md: '12px' },
            display: 'flex',
            alignItems: 'center',
            borderRadius: { xs: '9999px', md: '0.75rem' },
        },
        '& fieldset': {
            borderRadius: { xs: '9999px', md: '0.75rem' },
            borderColor: '#E2E8F0',
        },
        '&:hover fieldset': {
            borderColor: '#3F8EE8',
        },
        '&.Mui-focused fieldset': {
            borderColor: '#3F8EE8',
        },
        '& svg': {
            color: '#1E293B',
        },
        '&.Mui-disabled': {
            backgroundColor: '#F1F5F9',
        },
        '&.Mui-disabled .MuiSelect-select': {
            color: '#94A3B8',
        },
        '&.Mui-disabled svg': {
            color: '#94A3B8',
        },
    };

    const handleSubmit = (event: any) => {
        event.preventDefault();
        if (!info) return;

        const newInfo = toInformationUpdate(values, info);
        updateInformation.mutate(newInfo, {
            onSuccess: () => {
                setEdits({});
                i18n.changeLanguage(newInfo.language);
                setSaveAlert('success');
            },
            onError: () => setSaveAlert('error'),
        });
    };

    return (
        <div className='w-full px-4 py-4 sm:px-6 sm:py-6 lg:px-12 bg-[#F5F7FB] flex justify-center md:justify-start'>
            <form
                noValidate
                autoComplete='off'
                onSubmit={handleSubmit}
                className='w-full max-w-[520px] rounded-3xl bg-white px-5 py-6 sm:px-8 sm:py-8 shadow-md md:max-w-3xl md:rounded-none md:bg-transparent md:px-0 md:py-0 md:shadow-none'
            >
                <section className='space-y-5 md:space-y-6 md:border-b md:border-slate-300 md:pb-8'>
                    <div>
                        <p className='text-[#1E2B7C] text-xl font-semibold font-["Roboto"]'>{t('account')}</p>
                        <div className='mt-3 h-px w-full bg-slate-200' />
                    </div>
                    <div className='space-y-4'>
                        <div className='space-y-2'>
                            <p className='text-sm font-medium text-slate-600 font-["Roboto"]'>{t('displayName')}</p>
                            <input
                                className={inputBaseClasses}
                                type='text'
                                id='companyName'
                                name='companyName'
                                value={values.displayName}
                                onChange={edit('displayName')}
                            />
                        </div>
                        <div className='space-y-2'>
                            <p className='text-sm font-medium text-slate-600 font-["Roboto"]'>{t('email')}</p>
                            <input
                                className={`${inputBaseClasses} ${inputDisabledClasses}`}
                                type='text'
                                id='email'
                                name='email'
                                value={info?.email ?? ''}
                                disabled
                                readOnly
                            />
                        </div>
                        <div className='space-y-2'>
                            <p className='text-sm font-medium text-slate-600 font-["Roboto"]'>{t('phone')}</p>
                            <input
                                className={`${inputBaseClasses} ${inputDisabledClasses}`}
                                type='text'
                                id='phoneNumber'
                                name='phoneNumber'
                                value={info?.phone_number ?? ''}
                                disabled
                                readOnly
                            />
                        </div>
                    </div>
                </section>

                <section className='mt-8 space-y-5 md:mt-8 md:space-y-6 md:border-b md:border-slate-300 md:pb-8'>
                    <div>
                        <p className='text-[#1E2B7C] text-xl font-semibold font-["Roboto"]'>{t('preference')}</p>
                        <div className='mt-3 h-px w-full bg-slate-200' />
                    </div>
                    <div className='space-y-4'>
                        <div className='space-y-2'>
                            <p className='text-sm font-medium text-slate-600 font-["Roboto"]'>Language / 語言</p>
                            <FormControl
                                fullWidth
                                size='small'
                                variant='outlined'
                                sx={{ width: { xs: '100%', md: '400px' } }}
                            >
                                <Select
                                    native
                                    id='language'
                                    name='language'
                                    value={values.language}
                                    onChange={edit('language')}
                                    sx={selectBaseSx}
                                >
                                    <option value='english'>English</option>
                                    <option value='chinese'>中文</option>
                                    <option value='japanese'>日本語</option>
                                </Select>
                            </FormControl>
                        </div>
                        <div className='space-y-2'>
                            <p className='text-sm font-medium text-slate-600 font-["Roboto"]'>{t('tempUnit')}</p>
                            <FormControl
                                fullWidth
                                size='small'
                                variant='outlined'
                                sx={{ width: { xs: '100%', md: '400px' } }}
                            >
                                <Select
                                    native
                                    id='tempUnit'
                                    name='tempUnit'
                                    value={values.tempUnit}
                                    onChange={edit('tempUnit')}
                                    sx={selectBaseSx}
                                >
                                    <option value='celsius'>{t('celsius')}</option>
                                    <option value='fahrenheit'>{t('fahrenheit')}</option>
                                </Select>
                            </FormControl>
                        </div>
                        <div className='space-y-2'>
                            <p className='text-sm font-medium text-slate-600 font-["Roboto"]'>Entry Point</p>
                            <FormControl
                                fullWidth
                                size='small'
                                variant='outlined'
                                sx={{ width: { xs: '100%', md: '400px' } }}
                            >
                                <Select
                                    native
                                    id='entryPoint'
                                    name='entryPoint'
                                    value={values.entryPage}
                                    onChange={edit('entryPage')}
                                    sx={selectBaseSx}
                                >
                                    <option value='overview'>overview</option>
                                    <option value='uploaded'>uploaded successfully</option>
                                    <option value='pending'>not uploaded</option>
                                    <option value='realtime'>realtime</option>
                                    <option value='setting'>setting</option>
                                </Select>
                            </FormControl>
                        </div>
                        <div className='space-y-2'>
                            <p className='text-sm font-medium text-slate-600 font-["Roboto"]'>{t('timeZone')}</p>
                            <input
                                className={`${inputBaseClasses} ${inputDisabledClasses}`}
                                type='text'
                                id='timeZone'
                                name='timeZone'
                                value={timeZone}
                                disabled
                                readOnly
                            />
                        </div>
                    </div>
                </section>

                <LineConnectSection />

                <div className='mt-8 h-px w-full bg-slate-200 md:hidden' />

                <div className='mt-10 flex justify-center md:mt-10 md:justify-start'>
                    <button
                        type='submit'
                        disabled={!info || updateInformation.isPending}
                        className='h-12 w-full rounded-full bg-primary text-base font-semibold text-white shadow-md transition hover:bg-[#162061] disabled:cursor-not-allowed disabled:opacity-60 md:h-12 md:w-[200px] md:rounded-xl md:shadow-none'
                    >
                        {updateInformation.isPending ? t('saving') : t('save')}
                    </button>
                </div>
                {!info && loadFailed && (
                    <p role='alert' className='mt-4 text-sm text-red-500 font-["Roboto"]'>
                        {t('loadFail')}
                    </p>
                )}
                <div className='mt-6 h-px w-full'>version {import.meta.env.VITE_APP_VERSION ?? 'dev'}</div>
            </form>
            <Snackbar open={saveAlert !== null} autoHideDuration={6000} onClose={() => setSaveAlert(null)}>
                <Alert onClose={() => setSaveAlert(null)} severity={saveAlert ?? 'success'}>
                    {saveAlert === 'error' ? tNotify('saveFailed') : tNotify('saveSuccess')}
                </Alert>
            </Snackbar>
        </div>
    );
}

export default SettingsForm;
